import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, BookMarked } from "lucide-react";
import { subscribe, track, apiError } from "@/lib/api";
import { toast } from "sonner";

const DISMISS_KEY = "arw_note_dismissed";
const SHOW_AFTER_MS = 22000;
const COOLDOWN_DAYS = 7;

function recentlyDismissed() {
  const t = localStorage.getItem(DISMISS_KEY);
  if (!t) return false;
  return Date.now() - Number(t) < COOLDOWN_DAYS * 86400000;
}

export default function AuthorNotePopup() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [dossier, setDossier] = useState("");

  useEffect(() => {
    if (pathname.startsWith("/admin") || recentlyDismissed()) return;
    const t = setTimeout(() => setOpen(true), SHOW_AFTER_MS);
    return () => clearTimeout(t);
  }, [pathname]);

  const close = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await subscribe({ email });
      if (res.dossier_url) setDossier(res.dossier_url);
      toast.success(res.message || "You're on the list!");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.96 }}
          transition={{ duration: 0.4 }}
          data-testid="author-note-popup"
          className="fixed bottom-6 right-6 z-[70] w-[calc(100%-3rem)] max-w-sm border border-amber-500/40 bg-zinc-900 shadow-2xl"
        >
          <div className="grain absolute inset-0 pointer-events-none" />
          <button onClick={close} data-testid="close-author-note" className="absolute top-3 right-3 text-zinc-500 hover:text-zinc-200 z-10"><X size={18} /></button>
          <div className="relative p-6">
            <p className="text-xs tracking-accent uppercase text-amber-500 mb-2 inline-flex items-center gap-2"><BookMarked size={14} /> A note from A.R. Winfree</p>
            {dossier ? (
              <>
                <h3 className="font-display text-xl font-bold mb-3">You're in. Enjoy the Dossier.</h3>
                <a href={dossier} target="_blank" rel="noreferrer" data-testid="popup-dossier-download"
                  onClick={() => track("dossier_download", "/popup")}
                  className="inline-flex items-center gap-2 bg-amber-500 text-zinc-950 font-bold text-sm px-5 py-3 hover:bg-amber-400 transition-colors">
                  <Download size={15} /> Download the Dossier
                </a>
              </>
            ) : (
              <>
                <h3 className="font-display text-xl font-bold mb-2">Before you go into the dark…</h3>
                <p className="text-sm text-zinc-400 mb-4 leading-relaxed">Grab the free, spoiler-free Luminaria Dossier and join the reader list. First word on new releases, always.</p>
                <form onSubmit={submit} className="flex gap-2">
                  <input data-testid="popup-email" type="email" required placeholder="your@email.com" value={email} onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 bg-zinc-950 border border-zinc-700 px-3 py-2.5 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-amber-500" />
                  <button data-testid="popup-submit" type="submit" disabled={loading} className="bg-amber-500 text-zinc-950 font-bold text-sm px-4 py-2.5 hover:bg-amber-400 transition-colors disabled:opacity-60 whitespace-nowrap">
                    {loading ? "…" : "Get it"}
                  </button>
                </form>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
